'use client'

import {createContext, useContext} from 'react'

/**
 * The few strings the app chrome shows — header, nav, toasts, the locale
 * switch's error — handed down from the server as plain text (T-PERF-01).
 *
 * The chrome used to translate with next-intl's `useTranslations`, which put
 * the library's client runtime (~12 KB) and a message catalogue on every
 * signed-in page, the dashboard included, where nothing else needs them.
 * None of these strings take ICU arguments, so a lookup is all it takes.
 * Built by `getShellText()` (lib/shell-text.ts).
 */
export type ShellText = Record<'shell' | 'common' | 'errors', Record<string, string>>

const ShellTextContext = createContext<ShellText | null>(null)

export function ShellTextProvider({text, children}: {text: ShellText; children: React.ReactNode}) {
  return <ShellTextContext.Provider value={text}>{children}</ShellTextContext.Provider>
}

/** `t(key)` for one namespace; an unknown key renders as itself, like next-intl. */
export function useShellText(namespace: keyof ShellText) {
  const text = useContext(ShellTextContext)
  return (key: string) => text?.[namespace]?.[key] ?? `${namespace}.${key}`
}
